/**
 * 获取弹出窗口的容器元素。
 *
 * 该函数用于确定弹出窗口（如模态框、下拉菜单等）应该附加到哪个HTML元素上。
 * 默认情况下，弹出窗口会附加到`document.body`上。但是，可以通过传入一个节点来指定一个不同的容器。
 * 这个功能常见于需要将弹出窗口与特定的触发元素关联在一起的情况。
 *
 * @param node 可选参数，指定弹出窗口应该附加到的HTML元素。如果不传入此参数，默认会使用`document.body`。
 * @returns 返回用于附加弹出窗口的HTML元素。
 *
 * @source
 */
export function getPopupContainer(node?: HTMLElement): HTMLElement {
  // 如果传入了node且node有父节点，则将弹出窗口附加到node的父节点上；否则，附加到document.body上。
  return (node?.parentNode as HTMLElement) ?? document.body
}

/**
 * 打开一个新窗口或标签页。
 *
 * 此函数用于在浏览器中打开一个新的窗口或标签页，并允许指定一些额外的特性，如`noopener`和`noreferrer`，以增强安全性。
 * 默认情况下，新窗口将打开在名为`__blank`的标签页中，并启用`noopener`和`noreferrer`特性。
 *
 * @example
 * // 若当前要打开的URL为http://www.example.com/index.html，target属性为_self
 * openWindow('http://www.example.com/index.html', { target: '_self' })
 * // 则新窗口将打开在当前窗口中。
 *
 * @param url 要打开的URL。
 * @param opt 可选参数，用于指定新窗口的特性。
 * @param opt.target 可以是`_self`、`_blank`或其他自定义的窗口名称。默认为`__blank`。
 * @param opt.noopener 如果为true，则在新窗口中不设置`window.opener`属性。默认为true。
 * @param opt.noreferrer 如果为true，则在新窗口中不发送`referrerpolicy`。默认为true。
 *
 * @source
 */
export function openWindow(
  url: string,
  opt?: {
    target?: '_self' | '_blank' | string
    noopener?: boolean
    noreferrer?: boolean
  }
): void {
  // 解构赋值，设置默认值。如果没有提供opt，则target默认为'__blank'，noopener和noreferrer默认为true。
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {}
  // 用于存储新窗口特性的数组。
  const feature: string[] = []

  // 如果noopener为true，则添加noopener=yes到feature数组。
  noopener && feature.push('noopener=yes')
  // 如果noreferrer为true，则添加noreferrer=yes到feature数组。
  noreferrer && feature.push('noreferrer=yes')
  // 使用window.open打开新窗口，参数为url、target和feature数组通过join方法合并成的字符串。
  window.open(url, target, feature.join(','))
}

// emoji正则
const emojiRegex =
  // eslint-disable-next-line no-misleading-character-class
  /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}]|\p{Emoji_Presentation}/gu

/**
 * 移除字符串中的表情符号。
 *
 * 该函数通过正则表达式匹配并移除字符串中可能存在的表情符号。表情符号在各种上下文中可能会引起问题，
 * 比如在处理用户输入或生成某些格式的输出时。这个函数提供了一个简单的方法来确保字符串不包含表情符号。
 *
 * @param str 待处理的字符串，可能包含表情符号。
 * @returns 如果输入的字符串为null或undefined，则返回undefined；否则返回移除了表情符号的字符串。
 *
 * @example
 * replaceEmoji('Hello👋🏻') // 'Hello'
 *
 * @source
 */
export const replaceEmoji = (str: string): string => {
  // 使用可选链操作符(?.)确保在str为null或undefined时，函数安全返回undefined，而不是抛出错误。
  return str?.replace(emojiRegex, '')
}

/**
 * 队列，用于控制并发执行的任务数量。
 * limit 设置为 5 代表并发执行的任务最多不超过 5 个，有完成的任务就会自动从队列中取出一个任务开始执行，并保证并发数量不超过 5 个，直到队列为空。
 *
 * @example
 * const scheduler = new Scheduler(5);
 * scheduler.add(() => {
 *   console.log('Task 1');
 *   return new Promise((resolve) => {
 *     setTimeout(() => {
 *       resolve();
 *     }, 1000);
 *   });
 * });
 * scheduler.start()
 *
 * @source
 */
export class Scheduler {
  /**
   * 并发限制的数量。
   */
  private limit: number
  /**
   * 并发限制的备份，用于重置并发数
   */
  private limitCopy: number
  /**
   * 任务队列，存储待执行的任务。
   */
  private queue: Array<() => Promise<void>>

  /**
   * 构造函数初始化调度器。
   * @param limit 并发限制的数量，即同时最多允许执行的任务数量。
   */
  constructor(limit: number) {
    this.limit = limit
    this.limitCopy = limit
    this.queue = []
  }

  /**
   * 添加一个任务到调度器。
   * @param promiseCreator 一个函数，返回一个Promise，表示要执行的任务。
   * @returns 返回一个Promise，该Promise在任务完成时解决。
   */
  add(promiseCreator: () => Promise<void>): Promise<void> {
    return new Promise((resolve) => {
      this.queue.push(async () => {
        await promiseCreator()
        resolve()
      })
    })
  }

  /**
   * 开始执行任务。会根据并发限制，立即启动指定数量的任务。
   */
  start() {
    this.limit = this.limitCopy
    while (this.limit-- > 0) {
      this.next()
    }
  }

  /**
   * 执行队列中的下一个任务。如果队列不为空，则尝试执行队首的任务。
   * 如果任务执行成功，则继续执行下一个任务。如果任务执行失败，则尝试执行下一个任务。
   * 如果队列为空，则标记调度器为停止状态。
   * @returns 返回一个Promise，表示任务执行完成。
   */
  async next(): Promise<void> {
    if (this.queue.length) {
      try {
        await this.queue.shift()!()
        this.next()
      } catch {
        this.next()
      }
    }
  }
}

const hexList: string[] = []
for (let i = 0; i <= 15; i++) {
  hexList[i] = i.toString(16)
}

/**
 * 生成一个符合UUID规范的字符串。
 *
 * UUID（Universally Unique Identifier）是一种用于唯一标识的标准化格式，
 * 本函数旨在生成一个长度为36字符，由数字和字母组成的UUID字符串。
 *
 * @returns 生成的UUID字符串。
 * @example
 * buildUUID() // '48ecb2c1dff144b29e89113ec3728554'
 * buildUUID() // '98ec82c1dff144b29e89113ec3728787'
 * @source
 */
export function buildUUID(): string {
  let uuid = ''

  // 遍历36个位置，按照UUID的格式要求构建字符串
  for (let i = 1; i <= 36; i++) {
    // 在特定位置插入分隔符 "-"
    if (i === 9 || i === 14 || i === 19 || i === 24) {
      uuid += '-'
    } else if (i === 15) {
      // 在第15位固定为"4"
      uuid += 4
    } else if (i === 20) {
      // 在第20位随机选择一个0-8的数字
      uuid += hexList[(Math.random() * 4) | 8]
    } else {
      // 其他位置随机选择一个0-15的数字
      uuid += hexList[Math.floor(Math.random() * 16)]
    }
  }

  // 移除所有分隔符，返回最终的UUID字符串
  return uuid.replace(/-/g, '')
}

let unique = 0
/**
 * 构建一个短UUID。
 *
 * 该函数生成一个独特的短UUID，基于当前时间、随机数和一个自增的唯一值。
 * 这样可以确保生成的UUID在短时间内是唯一的。
 *
 * @param prefix - 可选参数，用于为UUID添加前缀，默认为空字符串。
 * @returns 返回一个字符串形式的短UUID。
 * @example
 * buildShortUUID() // '_28571877811719975925177'
 * buildShortUUID('prefix') // 'prefix__10195461331719975964382'
 * @source
 */
export function buildShortUUID(prefix = ''): string {
  // 获取当前时间戳
  const time = Date.now()
  // 生成一个随机数，确保UUID的随机性
  const random = Math.floor(Math.random() * 1000000000)
  // 自增唯一值，确保每个UUID都是唯一的
  unique++
  // 组合前缀、随机数、自增唯一值和时间戳，形成最终的UUID
  return `${prefix}_${random}${unique}${String(time)}`
}
